import { useState } from 'react';
import { supabase } from '../supabaseClient';
import type { CurrentUser } from '../types/currentUser';
import type { ShowToast, FetchData } from '../types/appShared';

// ══════════════════════════════════════════════
// useCommentsController — نفس فكرة useTasksController/useMaterialsController.
// ملاحظة: CommentsView.jsx بالفعل بتعمل أغلب شغل الحذف/الرد بنفسها جوّاها
// (باستخدام supabase مباشرة + fetchData/showToast/showConfirm اللي بتتبعت
// ليها كـ props)، فمش كل حاجة متعلقة بالـ comments محتاجة تتنقل هنا.
// commentsData/setCommentsData و commentReplies/setCommentReplies اتسابوا في
// App.jsx لأنهم بيتحمّلوا من fetchData المركزية (fetchData بتعمل
// setCommentReplies(data.commentReplies) مباشرة)، وبرضو الـ View نفسها بتعدّل
// commentReplies مباشرة بعد كل رد/حذف رد.
// ══════════════════════════════════════════════
interface UseCommentsControllerParams {
    currentProjectId: string | null;
    currentUser: CurrentUser | null;
    showToast: ShowToast;
    fetchData: FetchData;
}

export function useCommentsController({ currentProjectId, currentUser, showToast, fetchData }: UseCommentsControllerParams) {
    const [commentFilter, setCommentFilter] = useState('All');
    const [editingComment, setEditingComment] = useState<unknown>(null);
    const [replyInputs, setReplyInputs] = useState<Record<string, string>>({});

    const [newComment, setNewComment] = useState("");
    const [commentPriority, setCommentPriority] = useState("Medium");
    const [submittingComment, setSubmittingComment] = useState(false);

    const submitComment = async () => {
        if (!newComment.trim()) {
            showToast("Please enter a comment");
            return;
        }
        // ✅ تحقق صريح هنا (مكنش موجود قبل التحويل لـ TS) — لو currentUser
        // null (نادر، بس ممكن نظريًا) كان الكود القديم هيعمل crash فعلي على
        // currentUser.name تحت. دلوقتي بيفشل برسالة واضحة بدل exception.
        if (!currentUser) {
            showToast("You must be logged in to comment");
            return;
        }

        setSubmittingComment(true);

        try {
            const { error } = await supabase
                .from('comments')
                .insert([
                    {
                        project_id: currentProjectId,
                        username: currentUser.name,
                        position: currentUser.position,
                        department: currentUser.department,
                        comment: newComment,
                        priority: commentPriority || 'Normal',
                        avatar: currentUser.avatar,
                        timestamp: new Date().toISOString()
                    }
                ])
                .select();

            if (error) throw error;

            showToast("Comment submitted successfully!");
            setNewComment("");
            setCommentPriority("Normal");

            // Refresh comments
            fetchData();

        } catch (err: any) {
            console.error("Error submitting comment:", err);
            showToast("❌ Failed to submit comment. Please try again.");
        } finally {
            setSubmittingComment(false);
        }
    };

    return {
        commentFilter, setCommentFilter, editingComment, setEditingComment,
        replyInputs, setReplyInputs,
        newComment, setNewComment, commentPriority, setCommentPriority,
        submittingComment, setSubmittingComment, submitComment,
    };
}

export default useCommentsController;
